import { useState, useEffect } from "react";
import "./Donations.css";

const API_URL = "https://jspstrust.com/api/api/Donation.php"; // Update with your actual API URL

export default function Donations() {
    const [donations, setDonations] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState("create"); // "create" or "edit"
    const [currentDonation, setCurrentDonation] = useState({
        name: "",
        amount: "",
        date: new Date().toISOString().split('T')[0],
        paymentMethod: "cash",
        purpose: "general",
        status: "Received",
        notes: "",
        id: null
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Payment methods and purposes
    const paymentMethods = [
        { value: "cash", label: "Cash" },
        { value: "bank_transfer", label: "Bank Transfer" },
        { value: "cheque", label: "Cheque" },
        { value: "upi", label: "UPI" },
        { value: "credit_card", label: "Credit Card" },
        { value: "debit_card", label: "Debit Card" },
        { value: "online", label: "Online Payment" }
    ];

    const purposes = [
        { value: "general", label: "General Donation" },
        { value: "education", label: "Education Fund" },
        { value: "medical", label: "Medical Aid" },
        { value: "relief", label: "Relief Work" },
        { value: "event", label: "Event Sponsorship" },
        { value: "infrastructure", label: "Infrastructure" },
        { value: "other", label: "Other" }
    ];

    const statuses = [
        { value: "Received", label: "Received", color: "green" },
        { value: "Pending", label: "Pending", color: "orange" },
        { value: "Verified", label: "Verified", color: "blue" },
        { value: "Cancelled", label: "Cancelled", color: "red" }
    ];

    useEffect(() => {
        loadDonations();
    }, [currentPage, itemsPerPage]);

    // Reset to first page when filter or search changes
    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    }, [filter, search]);

    const loadDonations = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: itemsPerPage.toString()
            });

            const response = await fetch(`${API_URL}?${params.toString()}`);
            const result = await response.json();

            if (result.success) {
                setDonations(result.data || []);
                if (result.pagination) {
                    setTotalRecords(result.pagination.totalRecords || 0);
                    setTotalPages(result.pagination.totalPages || 1);
                }
            } else {
                console.error("Failed to load donations:", result.message);
                alert("Failed to load donations: " + (result.message || "Unknown error"));
            }
        } catch (error) {
            console.error("Error loading donations:", error);
            alert("Error loading donations. Please check your internet connection.");
        } finally {
            setLoading(false);
        }
    };

    const openCreateModal = () => {
        setModalType("create");
        setShowModal(true);
        setCurrentDonation({
            name: "",
            amount: "",
            date: new Date().toISOString().split('T')[0],
            paymentMethod: "cash",
            purpose: "general",
            status: "Received",
            notes: "",
            id: null
        });
        setErrors({});
    };

    const openEditModal = (donation) => {
        setModalType("edit");
        setShowModal(true);
        setCurrentDonation({
            id: donation.id,
            name: donation.name,
            amount: donation.amount.toString(),
            date: donation.date,
            paymentMethod: donation.payment_method || "cash",
            purpose: donation.purpose || "general",
            status: donation.status || "Received",
            notes: donation.notes || ""
        });
        setErrors({});
    };

    const closeModal = () => {
        setShowModal(false);
        setErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentDonation({
            ...currentDonation,
            [name]: value
        });

        // Clear error for this field
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ""
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!currentDonation.name.trim()) {
            newErrors.name = "Donor name is required";
        } else if (currentDonation.name.length < 2) {
            newErrors.name = "Name must be at least 2 characters";
        }

        if (!currentDonation.amount.trim()) {
            newErrors.amount = "Amount is required";
        } else if (isNaN(parseFloat(currentDonation.amount)) || parseFloat(currentDonation.amount) <= 0) {
            newErrors.amount = "Please enter a valid amount";
        }

        if (!currentDonation.date) {
            newErrors.date = "Date is required";
        }

        if (!currentDonation.paymentMethod) {
            newErrors.paymentMethod = "Payment method is required";
        }

        if (!currentDonation.purpose) {
            newErrors.purpose = "Purpose is required";
        }

        return newErrors;
    };

    const handleSaveDonation = async () => {
        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);

        const donationToSave = {
            name: currentDonation.name.trim(),
            amount: parseFloat(currentDonation.amount),
            date: currentDonation.date,
            paymentMethod: currentDonation.paymentMethod,
            purpose: currentDonation.purpose,
            status: currentDonation.status,
            notes: currentDonation.notes
        };

        try {
            let response;
            
            if (modalType === "create") {
                response = await fetch(API_URL, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(donationToSave)
                });
            } else {
                response = await fetch(API_URL, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        ...donationToSave,
                        id: currentDonation.id
                    })
                });
            }

            const result = await response.json();

            if (result.success) {
                alert(result.message || (modalType === "create" ? "Donation added successfully!" : "Donation updated successfully!"));
                loadDonations();
                closeModal();
            } else {
                alert("Error: " + (result.message || "Failed to save donation"));
            }
        } catch (error) {
            console.error("Error saving donation:", error);
            alert("Error saving donation. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this donation? This action cannot be undone.")) {
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(API_URL, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ id })
            });

            const result = await response.json();

            if (result.success) {
                alert(result.message || "Donation deleted successfully!");
                loadDonations();
            } else {
                alert("Error: " + (result.message || "Failed to delete donation"));
            }
        } catch (error) {
            console.error("Error deleting donation:", error);
            alert("Error deleting donation. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // Filter and search donations
    const filteredDonations = donations.filter(donation => {
        // Filter by status
        if (filter !== "all" && donation.status !== filter) {
            return false;
        }

        // Search by name
        if (search && !donation.name.toLowerCase().includes(search.toLowerCase())) {
            return false;
        }

        return true;
    });

    // Pagination handlers
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        
        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push('...');
                pages.push(currentPage - 1);
                pages.push(currentPage);
                pages.push(currentPage + 1);
                pages.push('...');
                pages.push(totalPages);
            }
        }
        
        return pages;
    };

    // Calculate statistics
    const totalDonations = donations.reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);
    const today = new Date().toISOString().split('T')[0];
    const todayDonations = donations.filter(d => d.date === today)
        .reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);
    const monthlyDonations = donations
        .filter(d => {
            const donationDate = new Date(d.date);
            const now = new Date();
            return donationDate.getMonth() === now.getMonth() &&
                donationDate.getFullYear() === now.getFullYear();
        })
        .reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);

    const getStatusColor = (status) => {
        const statusObj = statuses.find(s => s.value === status);
        return statusObj ? statusObj.color : "gray";
    };

    const getPurposeLabel = (purpose) => {
        const purposeObj = purposes.find(p => p.value === purpose);
        return purposeObj ? purposeObj.label : purpose;
    };

    const getPaymentMethodLabel = (method) => {
        const methodObj = paymentMethods.find(p => p.value === method);
        return methodObj ? methodObj.label : method;
    };

    return (
        <div className="donations">
            {/* Header Section */}
            <div className="donations-header">
                <h2><span>💰</span> Donation Management</h2>
                <button
                    className="create-donation-btn"
                    onClick={openCreateModal}
                    disabled={loading}
                >
                    <span className="btn-icon">+</span> New Donation
                </button>
            </div>

            {/* Statistics Cards */}
            <div className="donation-stats">
                <div className="stat-card total">
                    <div className="stat-icon">💰</div>
                    <div className="stat-content">
                        <h3>Total Donations</h3>
                        <p className="stat-value">{formatCurrency(totalDonations)}</p>
                        <p className="stat-subtext">From {totalRecords} donors</p>
                    </div>
                </div>

                <div className="stat-card today">
                    <div className="stat-icon">📅</div>
                    <div className="stat-content">
                        <h3>Today's Donations</h3>
                        <p className="stat-value">{formatCurrency(todayDonations)}</p>
                        <p className="stat-subtext">Today's collection</p>
                    </div>
                </div>

                <div className="stat-card monthly">
                    <div className="stat-icon">📊</div>
                    <div className="stat-content">
                        <h3>This Month</h3>
                        <p className="stat-value">{formatCurrency(monthlyDonations)}</p>
                        <p className="stat-subtext">Monthly total</p>
                    </div>
                </div>
            </div>

            {/* Filters and Search */}
            <div className="filters-section">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Search donors by name..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="search-input"
                    />
                    <span className="search-icon">🔍</span>
                </div>

                <div className="filter-tabs">
                    <button
                        className={`filter-tab ${filter === "all" ? "active" : ""}`}
                        onClick={() => setFilter("all")}
                    >
                        All
                    </button>
                    {statuses.map(status => (
                        <button
                            key={status.value}
                            className={`filter-tab ${filter === status.value ? "active" : ""}`}
                            onClick={() => setFilter(status.value)}
                            style={{
                                borderLeft: `4px solid ${status.color}`
                            }}
                        >
                            {status.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Donations List */}
            <div className="donations-list">
                <div className="list-header">
                    <h3>All Donations ({totalRecords})</h3>
                    <div className="header-actions">
                        <select 
                            className="items-per-page"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            disabled={loading}
                        >
                            <option value="10">10 per page</option>
                            <option value="20">20 per page</option>
                            <option value="50">50 per page</option>
                            <option value="100">100 per page</option>
                        </select>
                        <button
                            className="export-btn"
                            onClick={() => alert("Export feature coming soon!")}
                        >
                            📥 Export
                        </button>
                    </div>
                </div>

                {loading && donations.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">⏳</div>
                        <p>Loading donations...</p>
                    </div>
                ) : filteredDonations.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">💰</div>
                        <p>No donations found</p>
                        <p className="empty-subtext">
                            {search || filter !== "all"
                                ? "Try changing your search or filter"
                                : "Add your first donation to get started"}
                        </p>
                        {!search && filter === "all" && (
                            <button
                                className="create-first-btn"
                                onClick={openCreateModal}
                            >
                                Add First Donation
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="donations-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Donor Name</th>
                                        <th>Amount</th>
                                        <th>Date</th>
                                        <th>Payment Method</th>
                                        <th>Purpose</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredDonations.map(donation => (
                                        <tr key={donation.id}>
                                            <td>
                                                <div className="donor-cell">
                                                    <div className="donor-avatar">
                                                        {donation.name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="donor-info">
                                                        <strong>{donation.name}</strong>
                                                        {donation.notes && (
                                                            <small className="donor-notes">
                                                                {donation.notes.substring(0, 30)}{donation.notes.length > 30 ? '...' : ''}
                                                            </small>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="amount-cell">
                                                <strong>{formatCurrency(donation.amount)}</strong>
                                            </td>
                                            <td className="date-cell">
                                                {formatDate(donation.date)}
                                            </td>
                                            <td>
                                                <span className="payment-badge">
                                                    {getPaymentMethodLabel(donation.payment_method)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="purpose-badge">
                                                    {getPurposeLabel(donation.purpose)}
                                                </span>
                                            </td>
                                            <td>
                                                <span
                                                    className="status-badge"
                                                    style={{
                                                        backgroundColor: `${getStatusColor(donation.status)}20`,
                                                        color: getStatusColor(donation.status),
                                                        border: `1px solid ${getStatusColor(donation.status)}`
                                                    }}
                                                >
                                                    {donation.status}
                                                </span>
                                            </td>
                                            <td className="actions-cell">
                                                <button
                                                    className="edit-btn"
                                                    onClick={() => openEditModal(donation)}
                                                    title="Edit Donation"
                                                    disabled={loading}
                                                >
                                                    ✎ Edit
                                                </button>
                                                <button
                                                    className="delete-btn"
                                                    onClick={() => handleDelete(donation.id)}
                                                    title="Delete Donation"
                                                    disabled={loading}
                                                >
                                                    🗑 Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="pagination">
                                <div className="pagination-info">
                                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords} donations
                                </div>
                                <div className="pagination-controls">
                                    <button
                                        className="page-btn"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1 || loading}
                                    >
                                        « Previous
                                    </button>
                                    
                                    {getPageNumbers().map((page, index) => (
                                        page === '...' ? (
                                            <span key={`ellipsis-${index}`} className="page-ellipsis">...</span>
                                        ) : (
                                            <button
                                                key={page}
                                                className={`page-number ${currentPage === page ? 'active' : ''}`}
                                                onClick={() => handlePageChange(page)}
                                                disabled={loading}
                                            >
                                                {page}
                                            </button>
                                        )
                                    ))}
                                    
                                    <button
                                        className="page-btn"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages || loading}
                                    >
                                        Next »
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Modal for Create/Edit Donation */}
            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{modalType === "create" ? "Add New Donation" : "Edit Donation"}</h3>
                            <button
                                className="modal-close-btn"
                                onClick={closeModal}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="name">
                                        Donor Name <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={currentDonation.name}
                                        onChange={handleInputChange}
                                        placeholder="Enter donor name"
                                        className={errors.name ? "error" : ""}
                                    />
                                    {errors.name && (
                                        <span className="error-message">{errors.name}</span>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="amount">
                                        Amount (₹) <span className="required">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        id="amount"
                                        name="amount"
                                        value={currentDonation.amount}
                                        onChange={handleInputChange}
                                        placeholder="Enter amount"
                                        className={errors.amount ? "error" : ""}
                                        min="1"
                                        step="1"
                                    />
                                    {errors.amount && (
                                        <span className="error-message">{errors.amount}</span>
                                    )}
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="date">
                                        Date <span className="required">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        id="date"
                                        name="date"
                                        value={currentDonation.date}
                                        onChange={handleInputChange}
                                        className={errors.date ? "error" : ""}
                                    />
                                    {errors.date && (
                                        <span className="error-message">{errors.date}</span>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="paymentMethod">
                                        Payment Method <span className="required">*</span>
                                    </label>
                                    <select
                                        id="paymentMethod"
                                        name="paymentMethod"
                                        value={currentDonation.paymentMethod}
                                        onChange={handleInputChange}
                                        className={errors.paymentMethod ? "error" : ""}
                                    >
                                        <option value="">Select Payment Method</option>
                                        {paymentMethods.map(method => (
                                            <option key={method.value} value={method.value}>
                                                {method.label}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.paymentMethod && (
                                        <span className="error-message">{errors.paymentMethod}</span>
                                    )}
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="purpose">
                                        Purpose <span className="required">*</span>
                                    </label>
                                    <select
                                        id="purpose"
                                        name="purpose"
                                        value={currentDonation.purpose}
                                        onChange={handleInputChange}
                                        className={errors.purpose ? "error" : ""}
                                    >
                                        <option value="">Select Purpose</option>
                                        {purposes.map(purpose => (
                                            <option key={purpose.value} value={purpose.value}>
                                                {purpose.label}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.purpose && (
                                        <span className="error-message">{errors.purpose}</span>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="status">
                                        Status <span className="required">*</span>
                                    </label>
                                    <select
                                        id="status"
                                        name="status"
                                        value={currentDonation.status}
                                        onChange={handleInputChange}
                                        className={errors.status ? "error" : ""}
                                    >
                                        {statuses.map(status => (
                                            <option key={status.value} value={status.value}>
                                                {status.label}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.status && (
                                        <span className="error-message">{errors.status}</span>
                                    )}
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="notes">
                                    Notes (Optional)
                                </label>
                                <textarea
                                    id="notes"
                                    name="notes"
                                    value={currentDonation.notes}
                                    onChange={handleInputChange}
                                    placeholder="Add any additional notes about this donation..."
                                    rows="3"
                                />
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                className="cancel-btn"
                                onClick={closeModal}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                className="submit-btn"
                                onClick={handleSaveDonation}
                                disabled={loading}
                            >
                                {loading ? (
                                    <span className="loading-text">
                                        <span className="loading-spinner"></span>
                                        {modalType === "create" ? "Adding..." : "Updating..."}
                                    </span>
                                ) : (
                                    modalType === "create" ? "Add Donation" : "Update Donation"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}